import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LandingHeader from './LandingHeader'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Container,
  Chip,
  Divider,
  CircularProgress,
  Button,
  TextField,
  Skeleton,
  Tooltip,
  Collapse,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import WorkIcon from '@mui/icons-material/Work'
import BusinessIcon from '@mui/icons-material/Business'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import DescriptionIcon from '@mui/icons-material/Description'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AssessmentIcon from '@mui/icons-material/Assessment'
import SupportIcon from '@mui/icons-material/Support'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SecurityIcon from '@mui/icons-material/Security'
import SpeedIcon from '@mui/icons-material/Speed'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import PeopleIcon from '@mui/icons-material/People'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import GavelIcon from '@mui/icons-material/Gavel'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import CampaignIcon from '@mui/icons-material/Campaign'
import PhoneIcon from '@mui/icons-material/Phone'
import ShieldIcon from '@mui/icons-material/Shield'
import PublicIcon from '@mui/icons-material/Public'
import LaunchIcon from '@mui/icons-material/Launch'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import ReceiptIcon from '@mui/icons-material/Receipt'
import PolicyIcon from '@mui/icons-material/Policy'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import AssignmentIcon from '@mui/icons-material/Assignment'
import FeedbackIcon from '@mui/icons-material/Feedback'
import ChatIcon from '@mui/icons-material/Chat'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ShareIcon from '@mui/icons-material/Share'
import ScheduleIcon from '@mui/icons-material/Schedule'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import InsightsIcon from '@mui/icons-material/Insights'
import { dsVar, ink } from '../theme/tokens';
// Types
interface AIApplication {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tags: string[];
  category: string;
  path: string;
  featured?: boolean;
  aiPower: number;
  available: boolean;
  context?: string; // New prop to identify context (e.g., 'hr', 'banking')
  projectType?: string; // e.g., 'DATA_ANAL'
}

interface IndustrySegment {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  gradient: string;
  appCount: number;
  applications: AIApplication[];
}

const Apps = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSegment, setSelectedSegment] = useState<IndustrySegment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [comingSoonDialog, setComingSoonDialog] = useState<{ open: boolean; app: AIApplication | null }>({
    open: false,
    app: null
  });

  // Add CSS animation for blinking dot
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0.3; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);



  const industrySegments: IndustrySegment[] = [
    {
      id: 'banking',
      title: 'Banking & Financial Services',
      description: 'AI agents for core banking operations, loan processing and transaction analysis',
      icon: <span style={{ fontSize: 48 }}>🏦</span>,
      color: '#10B981',
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      appCount: 7,
      applications: [
        {
          id: 'loan-documents-processing',
          title: 'Loan Documents Processing',
          description: 'Streamline loan applications with intelligent document classification, analysis and automated information extraction to improve efficiency.',
          icon: <span style={{ fontSize: 24 }}>📋</span>,
          tags: ['Document Processing', 'Automation', 'Loan Management'],
          category: 'Banking & Financial Services',
          path: '/loan-processing',
          aiPower: 95,
          available: true
        },
        {
          id: 'company-research-assistant',
          title: 'Company Research',
          description: 'Extract key insights and facts from company\'s official statements, regulatory filings, press releases and online sources.',
          icon: <span style={{ fontSize: 24 }}>🔍</span>,
          tags: ['Research', 'Company Analysis', 'Due Diligence'],
          category: 'Banking & Financial Services',
          path: '/credit-checker',
          aiPower: 97,
          available: true
        },
        {
          id: 'credit-risk-analyser',
          title: 'Credit Risk Analyser',
          description: 'Analyze all customer documents and generate comprehensive risk profiles based on your rules and policies for informed lending decisions.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Risk Assessment', 'Credit Analysis', 'ML Models'],
          category: 'Banking & Financial Services',
          path: '/credit-risk',
          aiPower: 94,
          available: false
        },

        {
          id: 'transaction-insights-generator',
          title: 'Transaction Insights Generator',
          description: 'Analyze transaction patterns to identify customer opportunities for targeted offers and drive revenue growth.',
          icon: <span style={{ fontSize: 24 }}>💳</span>,
          tags: ['Transaction Analysis', 'Financial Insights', 'Data Analytics'],
          category: 'Banking & Financial Services',
          path: '/transaction-insights',
          aiPower: 92,
          available: false
        },
        {
          id: 'credit-underwriting-assistant',
          title: 'Credit Underwriting Assistant',
          description: 'AI-powered underwriting assistant for credit risk assessment and insurance decision making.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Underwriting', 'Credit Assessment', 'Risk Analysis'],
          category: 'Banking & Financial Services',
          path: '/credit-underwriting',
          aiPower: 93,
          available: false
        },

      ]
    },
    {
      id: 'hr',
      title: 'Human Resources/Talent Management',
      description: 'AI agents for intelligent employee pulse analytics, talent management and operational optimizations',
      icon: <span style={{ fontSize: 48 }}>👥</span>,
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      appCount: 4,
      applications: [
        {
          id: 'survey-insights-generator',
          title: 'Survey Insights Generator',
          description: 'Extract valuable insights from employee surveys using intelligent analysis to identify trends and sentiment.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Survey Analysis', 'Employee Insights', 'Sentiment Analysis'],
          category: 'Human Resources',
          path: '/survey-insights',
          aiPower: 93,
          available: true,
          context: 'hr'
        },
        {
          id: 'resume-ranking',
          title: 'Resume Ranking',
          description: 'Intelligent resume screening and ranking system to identify the best candidates based on job requirements.',
          icon: <span style={{ fontSize: 24 }}>📋</span>,
          tags: ['Resume Screening', 'Candidate Ranking', 'AI Recruitment'],
          category: 'Human Resources',
          path: '/resume-ranking',
          aiPower: 95,
          available: true
        },
        {
          id: 'policy-qa-assistant',
          title: 'Policy Q&A Assistant',
          description: 'Intelligent assistant to help employees quickly find answers to company policies and procedures.',
          icon: <span style={{ fontSize: 24 }}>❓</span>,
          tags: ['Policy Management', 'Q&A Assistant', 'Employee Support'],
          category: 'Human Resources',
          path: '/policy-qa',
          aiPower: 89,
          available: false
        },
        {
          id: 'job-description-optimiser',
          title: 'Job Description Optimiser',
          description: 'Attract top-fit candidates effortlessly with optimized job descriptions that boost quality and speed of hiring.',
          icon: <span style={{ fontSize: 24 }}>📝</span>,
          tags: ['Job Descriptions', 'Recruitment', 'Content Optimization'],
          category: 'Human Resources',
          path: '/job-description',
          aiPower: 91,
          available: false
        }
      ]
    },
    {
      id: 'government',
      title: 'Government & Public Sector',
      description: 'AI agents for secure document insights to enhance public administration efficiency',
      icon: <span style={{ fontSize: 48 }}>🏛️</span>,
      color: dsVar('fg-muted'),
      gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
      appCount: 4,
      applications: [
        {
          id: 'document-insights-assistant',
          title: 'Document Insights Assistant',
          description: 'Efficiently extract and analyse insights from official documents and public records.',
          icon: <span style={{ fontSize: 24 }}>📄</span>,
          tags: ['Document Analysis', 'Government Records', 'Data Extraction'],
          category: 'Government',
          path: '/docs-insights',
          aiPower: 91,
          available: true
        },
        {
          id: 'survey-insights-generator-gov',
          title: 'Citizen Survey Insights',
          description: 'Extract valuable insights from citizen surveys using intelligent analysis to identify trends and sentiment.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Survey Analysis', 'Citizen Insights', 'Sentiment Analysis'],
          category: 'Human Resources',
          path: '/survey-insights',
          aiPower: 93,
          available: true,
          context: 'gov'
        },
        {
          id: 'data-analytics',
          title: 'Data Analytics',
          description: 'Discover hidden patterns and generate actionable insights from public sector data using advanced AI analytics.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Data Analytics', 'Business Intelligence', 'Public Sector'],
          category: 'Government',
          path: '/data-analytics',
          aiPower: 94,
          available: true,
          context: 'gov',
          projectType: 'DATA_ANAL'
        },
        {
          id: 'asset-records-intelligence',
          title: 'Asset Records Intelligence',
          description: 'Comprehensive asset record management for government and public sector with intelligent document processing and ownership tracking.',
          icon: <span style={{ fontSize: 24 }}>🏠</span>,
          tags: ['Asset Management', 'Land Records', 'Property Documents', 'Ownership Tracking'],
          category: 'Government',
          path: '/asset-records-intelligence',
          aiPower: 94,
          available: true
        },
        {
          id: 'public-feedback-analyser',
          title: 'Public Feedback Analyser',
          description: 'Extract valuable insights from citizen opinions and public feedback to identify areas for improving services and policies.',
          icon: <span style={{ fontSize: 24 }}>📈</span>,
          tags: ['Public Opinion', 'Feedback Analysis', 'Citizen Engagement'],
          category: 'Government',
          path: '/public-feedback',
          aiPower: 88,
          available: false
        },
        {
          id: 'digital-assistant-citizen-faqs',
          title: 'Digital Assistant for Citizen FAQs',
          description: 'AI-powered digital assistant to help citizens with frequently asked questions and government services.',
          icon: <span style={{ fontSize: 24 }}>🤖</span>,
          tags: ['Citizen Services', 'FAQ Assistant', 'Government Support'],
          category: 'Government',
          path: '/citizen-faqs',
          aiPower: 90,
          available: false
        }
      ]
    },
    {
      id: 'telecom',
      title: 'Telecom',
      description: 'AI agents for enhancing customer experience and driving revenue growth',
      icon: <span style={{ fontSize: 48 }}>📡</span>,
      color: '#06B6D4',
      gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
      appCount: 1,
      applications: [
        {
          id: 'customer-support-assistant-banking',
          title: 'Support Assistant',
          description: 'Intelligent assistant for common customer queries and account management support.',
          icon: <span style={{ fontSize: 24 }}>🤖</span>,
          tags: ['Customer Service', 'Support', 'Account Management'],
          category: 'Banking & Financial Services',
          path: '/customer-service',
          aiPower: 96,
          available: true
        }
      ]
    },
    {
      id: 'Customer',
      title: 'Customer Experience',
      description: 'AI agents for customer support, personalization, and enhancing experience across the customer journey',
      icon: <span style={{ fontSize: 48 }}>🛡️</span>,
      color: '#3B82F6',
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      appCount: 1,
      applications: [
        {
          id: 'survey-insights-generator-banking',
          title: 'Customer Survey Insights',
          description: 'Intelligently analyze customer surveys to extract valuable insights, sentiments, and trends.',
          icon: <span style={{ fontSize: 24 }}>📊</span>,
          tags: ['Customer Insights', 'Survey Analysis', 'Customer Sentiment '],
          category: 'Customer Experience',
          path: '/survey-insights',
          aiPower: 93,
          available: true,
          context: 'banking'
        },
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing',
      description: 'AI agents for content creation, campaign optimization and competitive insights',
      icon: <span style={{ fontSize: 48 }}>📢</span>,
      color: '#EC4899',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
      appCount: 1,
      applications: [
        {
          id: 'news-analysis',
          title: 'News Analysis',
          description: 'Discover hidden patterns and generate actionable insights from your data using advanced AI analytics.',
          icon: <span style={{ fontSize: 24 }}>🔍</span>,
          tags: ['Data Analytics', 'Business Intelligence', 'Pattern Recognition'],
          category: 'Marketing',
          path: '/news-analysis',
          aiPower: 94,
          available: true
        }
      ]
    }
  ];

  // Handle navigation context restoration and browser navigation
  React.useEffect(() => {
    // Function to restore context
    const restoreContext = () => {
      const context = sessionStorage.getItem('appsNavigationContext');
      console.log('🔍 Checking navigation context:', context);

      if (context && context.startsWith('industry:')) {
        const industryId = context.split(':')[1];
        console.log('🏭 Restoring industry context:', industryId);

        const segment = industrySegments.find(s => s.id === industryId);
        if (segment) {
          console.log('✅ Found segment, setting selectedSegment:', segment.title);
          setSelectedSegment(segment);
        } else {
          console.log('❌ Segment not found for ID:', industryId);
        }
      } else {
        console.log('ℹ️ No industry context found, staying on main apps');
      }
    };

    // Check if user manually went back to main apps OR if we already have a selectedSegment
    const hasManuallyGoneBack = sessionStorage.getItem('userWentBackToMain');
    const alreadyHasSegment = selectedSegment !== null;

    console.log('🔍 useEffect: hasManuallyGoneBack =', hasManuallyGoneBack);
    console.log('🔍 useEffect: alreadyHasSegment =', alreadyHasSegment);
    console.log('🔍 useEffect: selectedSegment =', selectedSegment?.title);

    if (!hasManuallyGoneBack && !alreadyHasSegment) {
      // Restore context when component mounts or industrySegments change
      // Small delay to ensure component is fully ready
      setTimeout(restoreContext, 100);
    } else {
      console.log('❌ Skipping context restoration because:', {
        hasManuallyGoneBack,
        alreadyHasSegment,
        selectedSegmentTitle: selectedSegment?.title
      });
    }

    // Handle browser back/forward button
    const handlePopState = () => {
      console.log('🔄 Browser navigation detected, restoring context...');
      // Small delay to ensure the component is ready
      setTimeout(restoreContext, 200);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Clean up flags when component unmounts
      sessionStorage.removeItem('userWentBackToMain');
    };
  }, [industrySegments]);

  const filteredSegments = industrySegments.filter(segment =>
    segment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    segment.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAppClick = async (application: AIApplication) => {
    if (!application.available) {
      setComingSoonDialog({ open: true, app: application });
      return;
    }

    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Store the current context (whether we're in industry view or main apps)
      const context = selectedSegment ? `industry:${selectedSegment.id}` : 'main';
      console.log('💾 Storing navigation context:', context, 'for segment:', selectedSegment?.title);
      sessionStorage.setItem('appsNavigationContext', context);

      // Store app context if it exists (hr, banking, etc.)
      if (application.context) {
        sessionStorage.setItem('appContext', application.context);
        console.log('💾 Storing app context:', application.context);
      }

      // Clear the manual back flag when navigating to an app
      sessionStorage.removeItem('userWentBackToMain');

      navigate(application.path);
      setFeedback({ type: 'success', message: `${application.title} launched successfully!` });
    } catch (error) {
      setFeedback({ type: 'error', message: 'Failed to launch app' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToMainApps = () => {
    console.log('🔙 Going back to main apps, clearing context');
    console.log('🔍 Before: selectedSegment =', selectedSegment?.title);
    console.log('🔍 Before: context =', sessionStorage.getItem('appsNavigationContext'));

    // Clear everything immediately
    setSelectedSegment(null);
    sessionStorage.setItem('userWentBackToMain', 'true');
    sessionStorage.removeItem('appsNavigationContext');
    sessionStorage.removeItem('appContext'); // Clear app context too

    // Force a re-render by updating a dummy state
    setTimeout(() => {
      console.log('🔍 After timeout: selectedSegment =', selectedSegment?.title);
      console.log('🔍 After timeout: context =', sessionStorage.getItem('appsNavigationContext'));
      console.log('🔍 After timeout: userWentBackToMain =', sessionStorage.getItem('userWentBackToMain'));
    }, 0);
  };

  const ComingSoonDialog = () => (
    <Dialog
      open={comingSoonDialog.open}
      onClose={() => setComingSoonDialog({ open: false, app: null })}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: dsVar('on-accent') }}>
          Coming Soon
        </Typography>
        <IconButton
          onClick={() => setComingSoonDialog({ open: false, app: null })}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {comingSoonDialog.app && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              {comingSoonDialog.app.icon}
            </Box>

            <Typography variant="h4" sx={{
              fontWeight: 700,
              color: dsVar('on-accent'),
              mb: 2
            }}>
              {comingSoonDialog.app.title}
            </Typography>

            <Typography variant="body1" sx={{
              color: dsVar('fg-muted'),
              mb: 3,
              lineHeight: 1.6
            }}>
              {comingSoonDialog.app.description}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 3 }}>
              {comingSoonDialog.app.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(21, 157, 255, 0.1)',
                    borderRadius: '5px !important',
                    color: ink('#159DFF'),
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                />
              ))}
            </Box>

            <Box sx={{
              backgroundColor: 'rgba(156, 39, 176, 0.1)',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid rgba(156, 39, 176, 0.2)'
            }}>
              <Typography variant="h6" sx={{
                color: ink('#9C27B0'),
                fontWeight: 600,
                mb: 1
              }}>
                🚀 Coming Soon
              </Typography>
              <Typography variant="body2" sx={{
                color: ink('#9C27B0'),
                opacity: 0.8
              }}>
                This application is currently in development. We're working hard to bring you this powerful AI solution soon!
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={() => setComingSoonDialog({ open: false, app: null })}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
            borderRadius: '8px',
            '&:hover': {
              background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)'
            }
          }}
        >
          Get Notified
        </Button>
      </DialogActions>
    </Dialog>
  );

  const IndustrySegmentCard = ({ segment }: { segment: IndustrySegment }) => (
    <div
      onClick={() => setSelectedSegment(segment)}
      className="bg-surface p-6 rounded-2xl shadow-sm border border-fg-2/20 hover:border-accent-fg/50 transition-all cursor-pointer h-full flex flex-col gap-4 group"
    >
      <div className="flex items-center gap-4 mb-2">
        <div className="p-3 bg-fg/5 text-accent-fg rounded-xl group-hover:scale-105 transition-transform flex items-center justify-center">
          {segment.icon}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-fg mb-1">{segment.title}</h3>
          {segment.appCount > 0 && (
            <span className="px-3 py-1 bg-accent/10 text-accent-fg text-xs font-bold rounded-full">
              {segment.appCount === 1 ? '1 AI Agent' : `${segment.appCount} AI Agents`}
            </span>
          )}
        </div>
      </div>
      <p className="text-sm text-fg-2 flex-1">
        {segment.description}
      </p>
      {segment.appCount > 0 ? (
        <div className="flex items-center justify-between text-accent-fg font-medium text-sm mt-4 pt-4 border-t border-fg/10">
          <span>Explore AI Agents</span>
          <LaunchIcon sx={{ fontSize: '14px' }} />
        </div>
      ) : (
        <div className="flex items-center gap-2 text-fg-2 font-medium text-sm mt-4 pt-4 border-t border-fg/10">
          <div className="w-1.5 h-1.5 rounded-full bg-fg-2 animate-pulse"></div>
          <span>Coming Soon</span>
          <ScheduleIcon sx={{ fontSize: '14px', ml: 'auto' }} />
        </div>
      )}
    </div>
  );


  const ApplicationCard = ({ application }: { application: AIApplication }) => (
    <div className="bg-surface p-6 rounded-2xl shadow-sm border border-fg-2/20 hover:border-accent-fg/50 transition-all flex flex-col h-full group relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-fg/5 text-accent-fg rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          {application.icon}
        </div>
        <div>
          <h3 className="font-bold text-base text-fg">{application.title}</h3>
        </div>
      </div>

      <p className="text-sm text-fg-2 mb-6 flex-1 line-clamp-3">
        {application.description}
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {application.tags.slice(0, 3).map((tag, i) => (
          <span key={i} className="px-2 py-1 bg-fg/5 text-fg-2 text-[10px] font-medium rounded border border-fg/10">
            {tag}
          </span>
        ))}
        {application.tags.length > 3 && (
          <span className="px-2 py-1 bg-fg/5 text-fg-2 text-[10px] font-medium rounded border border-fg/10 opacity-60">
            +{application.tags.length - 3} more
          </span>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleAppClick(application);
        }}
        className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all flex justify-center items-center ${application.available
            ? 'bg-accent text-on-accent hover:bg-[#ffe57a] shadow-lg shadow-accent/10'
            : 'bg-fg/5 text-fg-2 border border-fg/10 cursor-not-allowed'
          }`}
      >
        {application.available ? 'Launch AI Agent' : 'Coming Soon'}
      </button>
    </div>
  );


  const SegmentDetailView = ({ segment }: { segment: IndustrySegment }) => (
    <div className="min-h-[calc(100vh-50px)] bg-page p-8 pb-48 font-sans">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="flex flex-col mb-8">
          <button
            onClick={handleBackToMainApps}
            className="flex items-center gap-2 text-fg-2 hover:text-fg font-medium text-sm transition-colors w-fit mb-8"
          >
            <ArrowBackIosIcon sx={{ fontSize: '14px' }} />
            Back to All Industries
          </button>

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-fg/5 rounded-2xl flex items-center justify-center text-accent-fg border border-fg/10">
                {segment.icon}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-fg mb-2">{segment.title}</h1>
                <p className="text-fg-2 text-base">{segment.description}</p>
              </div>
            </div>

            <span className="px-4 py-2 bg-accent/10 text-accent-fg font-bold rounded-lg whitespace-nowrap border border-accent-fg/20">
              {segment.appCount > 0 ? (segment.appCount === 1 ? '1 AI Agent' : `${segment.appCount} AI Agents`) : 'Coming Soon'}
            </span>
          </div>
        </div>

        {segment.applications.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {segment.applications.map((app) => (
              <div key={app.id}>
                <ApplicationCard application={app} />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface border border-dashed border-fg-2/30 rounded-2xl p-12 text-center max-w-2xl mx-auto">
            <div className="w-24 h-24 mx-auto bg-fg/5 rounded-full flex items-center justify-center text-accent-fg mb-6 text-4xl">
              ⚡
            </div>
            <h2 className="text-2xl font-bold text-fg mb-4">Coming Soon</h2>
            <p className="text-fg-2 mb-8 leading-relaxed max-w-md mx-auto">
              We're working hard to bring you powerful AI agents for {segment.title.toLowerCase()}. Stay tuned for updates!
            </p>
            <div className="flex items-center justify-center gap-3">
              <span className="px-3 py-1.5 bg-fg/5 text-fg text-xs font-bold rounded-lg border border-fg/10">In Development</span>
              <span className="px-3 py-1.5 bg-fg/5 text-fg text-xs font-bold rounded-lg border border-fg/10">AI-Powered</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );


  if (selectedSegment) {
    return (
      <div>
        <LandingHeader selectedPage="Apps" />
        <SegmentDetailView segment={selectedSegment} />
        <ComingSoonDialog />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page font-sans flex flex-col m-0 p-0 overflow-hidden text-left">
      <LandingHeader selectedPage="Apps" />

      <div className="flex-1 overflow-y-auto w-full p-8 pb-32">
        <div className="max-w-7xl mx-auto space-y-12">

          <div className="flex gap-4 items-start max-w-3xl">
            <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent-fg flex items-center justify-center shrink-0 border border-accent-fg/20">
              <AutoAwesomeIcon sx={{ fontSize: '18px' }} />
            </div>
            <div className="bg-surface p-4 rounded-xl shadow-sm border border-fg-2/20 text-sm leading-relaxed text-fg-2">
              <span className="text-fg font-bold block mb-1">AI Agents for Every Industry</span>
              Discover powerful AI agents designed for your industry. Streamline operations, uncover insights, and accelerate growth with smarter decision making.
            </div>
          </div>

          <div className="flex flex-col space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <h2 className="text-xl font-bold text-fg tracking-tight">Industry Sectors Directory</h2>

              <div className="w-full md:w-80 relative">
                <input
                  type="text"
                  placeholder="Search industry segments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-surface border border-fg-2/30 text-fg rounded-lg h-10 pl-10 pr-4 text-sm focus:outline-none focus:border-accent-fg focus:ring-1 focus:ring-accent-fg placeholder:text-fg-2/50 transition-all font-sans"
                />
                <SearchIcon sx={{ color: dsVar('fg-2'), position: 'absolute', left: '12px', top: '10px', fontSize: '20px' }} />
              </div>
            </div>

            {(() => {
              const context = sessionStorage.getItem('appsNavigationContext');
              if (context && context.startsWith('industry:')) {
                const industryId = context.split(':')[1];
                const segment = industrySegments.find(s => s.id === industryId);
                return (
                  <button
                    onClick={handleBackToMainApps}
                    className="flex items-center gap-2 text-fg-2 hover:text-fg font-medium text-sm transition-colors w-fit"
                  >
                    <ArrowBackIcon sx={{ fontSize: '14px' }} />
                    Back to {segment?.title || 'Industry'} View
                  </button>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSegments.map((segment) => (
                <div key={segment.id}>
                  <IndustrySegmentCard segment={segment} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16 bg-surface rounded-2xl p-8 border border-fg/5 flex flex-col md:flex-row items-center justify-between gap-8 text-left shadow-lg">
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-fg mb-2 font-mono">Ready to Transform Your Business?</h3>
              <p className="text-fg-2 text-sm">
                Explore our enterprise-grade AI agents and discover how they can streamline your operations and drive measurable business outcomes.
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-fg/5 rounded-lg border border-fg/10 text-fg font-medium text-xs">
                <SecurityIcon sx={{ fontSize: '16px', color: dsVar('accent-fg') }} />
                Enterprise Security
              </div>
              <div className="flex items-center gap-2 px-4 py-2.5 bg-fg/5 rounded-lg border border-fg/10 text-fg font-medium text-xs">
                <InsightsIcon sx={{ fontSize: '16px', color: dsVar('accent-fg') }} />
                Performance Optimized
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Feedback Snackbar */}
      <Snackbar
        open={!!feedback}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback(null)}
          severity={feedback?.type}
          sx={{ width: '100%' }}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>

      <ComingSoonDialog />
    </div>
  )
}

export default Apps
